import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  loadDashboardLayout,
  saveDashboardLayout,
} from "../../Redux/Actions/ActionStatistics";
import "./DashboardBuilder.css";

/**
 * FE-12 — Dashboard Builder
 * Drag-and-drop widget layout editor for the main dashboard page.
 * Persists the user's preferred widget order and visibility.
 *
 * Props:
 *   translate — i18n function
 */

/* Available dashboard widgets */
const ALL_WIDGETS = [
  { key: "general_statistics", labelKey: "General_statistics", icon: "la-chart-bar" },
  { key: "scientific_office", labelKey: "Scientific_Office", icon: "la-building" },
  { key: "work_plan_stats", labelKey: "work_plan_statistics", icon: "la-clipboard-list" },
  { key: "target_group_report", labelKey: "target_group_report", icon: "la-users" },
  { key: "recent_reports", labelKey: "smart_cards_recent", icon: "la-clock" },
  { key: "favorites", labelKey: "smart_cards_favorites", icon: "la-star" },
  { key: "scheduled_reports", labelKey: "scheduled_reports_title", icon: "la-bell" },
];

/* Default layout if none saved */
const DEFAULT_LAYOUT = ALL_WIDGETS.map((w) => ({
  key: w.key,
  visible: true,
}));

function DashboardBuilder({ translate }) {
  const dispatch = useDispatch();
  const savedLayout = useSelector((s) => s.statisticss.dashboardLayout);

  const [widgets, setWidgets] = useState(DEFAULT_LAYOUT);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);

  /* Load saved layout on mount */
  useEffect(() => {
    dispatch(loadDashboardLayout());
  }, [dispatch]);

  /* Sync from Redux when saved layout arrives */
  useEffect(() => {
    if (savedLayout && savedLayout.widgets) {
      setWidgets(savedLayout.widgets);
      setDirty(false);
    }
  }, [savedLayout]);

  /* Drag-and-drop handler */
  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;
    setWidgets((prev) => {
      const next = Array.from(prev);
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
    setDirty(true);
  }, []);

  /* Toggle widget visibility */
  const toggleVisibility = (key) => {
    setWidgets((prev) =>
      prev.map((w) => (w.key === key ? { ...w, visible: !w.visible } : w))
    );
    setDirty(true);
  };

  /* Save layout to backend */
  const handleSave = () => {
    dispatch(saveDashboardLayout(widgets));
    setDirty(false);
    setEditing(false);
  };

  /* Reset to default */
  const handleReset = () => {
    setWidgets(DEFAULT_LAYOUT);
    setDirty(true);
  };

  return (
    <div className="dashboard-builder">
      <div className="db-header">
        <span className="las la-th-large"></span>
        <span>{translate("dashboard_builder_title")}</span>
        <div className="db-header-actions">
          {editing && (
            <>
              <button className="db-btn db-btn-reset" onClick={handleReset}>
                <span className="las la-undo"></span>
                {translate("dashboard_builder_reset")}
              </button>
              <button
                className="db-btn db-btn-save"
                onClick={handleSave}
                disabled={!dirty}
              >
                <span className="las la-check"></span>
                {translate("templates_save")}
              </button>
            </>
          )}
          <button
            className="db-btn db-btn-edit"
            onClick={() => setEditing((p) => !p)}
          >
            <span className={`las ${editing ? "la-times" : "la-cog"}`}></span>
            {editing ? translate("back") : translate("dashboard_builder_customize")}
          </button>
        </div>
      </div>

      {/* Widget list (editable in editing mode) */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="dashboard-widgets">
          {(provided) => (
            <div
              className="db-widget-list"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {widgets.map((w, index) => {
                const meta = ALL_WIDGETS.find((a) => a.key === w.key);
                if (!meta) return null;
                return (
                  <Draggable key={w.key} draggableId={w.key} index={index}>
                    {(prov, snap) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        className={`db-widget${w.visible ? "" : " hidden"}${
                          snap.isDragging ? " dragging" : ""
                        }`}
                      >
                        <span className={`db-widget-icon las ${meta.icon}`}></span>
                        <span className="db-widget-label">
                          {translate(meta.labelKey)}
                        </span>
                        {editing && (
                          <button
                            className="db-widget-toggle"
                            onClick={() => toggleVisibility(w.key)}
                          >
                            <span
                              className={`las ${
                                w.visible ? "la-eye" : "la-eye-slash"
                              }`}
                            ></span>
                          </button>
                        )}
                        {editing && (
                          <span className="db-drag-handle las la-grip-vertical"></span>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}

export default DashboardBuilder;
export { ALL_WIDGETS, DEFAULT_LAYOUT };
